import * as Sentry from "@sentry/react";
import { useT } from "@transifex/react";
import moment, { type Moment } from "moment";
import type { StudentTestSharingData, TeacherTestDetails } from "p6m-p6u";
import { type Dispatch, type SetStateAction, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { ClassActivityCategory } from "../constants/ClassActivityConstants";
import { useGeneralContext } from "../context/AppGeneralContext";
import { useTestCreationContext } from "../context/TestCreationContext";
import { useRTContext } from "../context/RTContext";
import useToast from "./useToast";
import { PreviewView } from "../enums/views";
import { ShareModes, ShareSteps, ShareTimings } from "../enums/shareFlow";
import { SchoolClassVisibilities } from "../enums/schoolClasses";
import {
    ActivityTypeProps,
    SHARE_STEP_PROPS,
    toClassVisibilityProp,
    toIsFirstClassProps,
} from "../helpers/analyticsEventProps";
import { ToastVariants } from "../enums/toast";
import { getUniqueDefaultClassName } from "../utils/schoolClassUtils";
import { getStudentTestUrl } from "../helpers/ClassActivityMapping";
import { getCreationReturnPath } from "../helpers/navigationHelper";
import {
    collapseClassForm,
    executeShareWithClassRollback,
    hasEffectiveClass,
    hasEffectivePrivateClass,
    requiresPrivateClass,
    resolveEmptyClassInviteModal,
    ShareWithClassRollbackStages,
} from "../helpers/ShareFlow";
import { toVisibilityDateMs } from "../helpers/ActivityVisibility";
import { resolveShareModeWithLegacy } from "../helpers/LegacyTest";
import { useShareFlowState } from "./useShareFlowState";
import { useLogger } from "./useLogger";
import type {
    DetailsSlice,
    EditTestDetailsState,
    SharePopupState,
} from "../complex/classActivityCard/ClassActivityCardsOverview.types";

export const INITIAL_TIME_VALUE = { display: "", value: 0 };

const INITIAL_SHARE_POPUP_STATE: SharePopupState = {
    testId: null,
    testName: "",
    step: ShareSteps.MODE,
    selectedMode: null,
    selectedSchoolClassId: null,
    infoExpanded: false,
    timing: ShareTimings.NOW,
    visibilityDate: null,
    message: "",
    deadline: {
        show: false,
        value: null,
    },
    duration: {
        show: false,
        hours: INITIAL_TIME_VALUE,
        minutes: INITIAL_TIME_VALUE,
    },
    newClassForm: null,
};

const SHARE_STEP_ORDER = [ShareSteps.MODE, ShareSteps.CLASS, ShareSteps.TIMING, ShareSteps.DETAILS];

const _getAdjacentStepIndex = (currentStep: ShareSteps, direction: 1 | -1): number | null => {
    const currentIndex = SHARE_STEP_ORDER.indexOf(currentStep);
    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= SHARE_STEP_ORDER.length) return null;
    return nextIndex;
};

// Practice + no class has nothing to schedule — the teacher just copies the link — so timing is skipped.
const _shouldSkipTimingStep = (state: SharePopupState): boolean =>
    state.selectedMode === ShareModes.PRACTICE && !hasEffectiveClass(state.selectedSchoolClassId, state.newClassForm);

const SHARE_TOAST_DURATION_CLASS_MS = 3000;
const SHARE_TOAST_DURATION_LINK_COPIED_MS = 7000;
const SHARE_TOAST_DURATION_ERROR_MS = 5000;

const INITIAL_EDIT_TEST_DETAILS_STATE: EditTestDetailsState = {
    testId: null,
    shareMode: null,
    message: "",
    deadline: { show: false, value: null },
    duration: { show: false, hours: INITIAL_TIME_VALUE, minutes: INITIAL_TIME_VALUE },
};

const _pad2 = (n: number) => n.toString().padStart(2, "0");

// Shared test timeLimit is persisted as "HH:mm" (e.g. "02:30"). Inverse of _serializeDetails.timeLimit.
// "00:00" round-trips to "no duration" so a teacher toggling the section on without picking values
// doesn't degrade BE state into a 0-time test.
const _parseTimeLimit = (timeLimit: string | undefined | null) => {
    if (!timeLimit) return null;
    const [hoursStr, minutesStr] = timeLimit.split(":");
    const hours = Number(hoursStr);
    const minutes = Number(minutesStr);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;
    if (hours === 0 && minutes === 0) return null;
    return {
        hours: { display: `${hours} (h)`, value: hours },
        minutes: { display: `${_pad2(minutes)} (min)`, value: minutes },
    };
};

export interface DetailHandlers {
    onMessageChange: (message: string) => void;
    onToggleDeadline: () => void;
    onDeadlineChange: (value: Moment | null) => void;
    onToggleDuration: () => void;
    onDurationHoursSelect: (value: number) => void;
    onDurationMinutesSelect: (value: number) => void;
}

// Both share-popup and edit-popup state contain the same {message, deadline, duration} slice. This factory
// produces the six setters keyed off whichever setState is passed in, so the two flows can stay in lock-step.
const _makeDetailHandlers = <S extends DetailsSlice>(setState: Dispatch<SetStateAction<S>>): DetailHandlers => ({
    onMessageChange: (message) => setState((p) => ({ ...p, message })),
    onToggleDeadline: () =>
        setState((p) => ({
            ...p,
            deadline: { show: !p.deadline.show, value: p.deadline.show ? null : p.deadline.value },
        })),
    onDeadlineChange: (value) => setState((p) => ({ ...p, deadline: { ...p.deadline, value } })),
    onToggleDuration: () =>
        setState((p) => ({
            ...p,
            duration: {
                show: !p.duration.show,
                hours: p.duration.show ? INITIAL_TIME_VALUE : p.duration.hours,
                minutes: p.duration.show ? INITIAL_TIME_VALUE : p.duration.minutes,
            },
        })),
    onDurationHoursSelect: (value) => {
        const hours = value / 60;
        setState((p) => ({
            ...p,
            duration: { ...p.duration, hours: { display: `${hours} (h)`, value: hours } },
        }));
    },
    onDurationMinutesSelect: (value) =>
        setState((p) => ({
            ...p,
            duration: {
                ...p.duration,
                minutes: { display: `${_pad2(value)} (min)`, value },
            },
        })),
});

const _serializeDetails = (details: DetailsSlice) => {
    const { hours, minutes } = details.duration;
    const hasDuration = details.duration.show && (hours.value > 0 || minutes.value > 0);
    return {
        dueDate: details.deadline.show && details.deadline.value ? details.deadline.value.valueOf() : 0,
        timeLimit: hasDuration ? `${_pad2(hours.value)}:${_pad2(minutes.value)}` : "",
        teacherComment: details.message.trim(),
    };
};

export enum TestPopupActions {
    DELETE = "DELETE",
    DUPLICATE = "DUPLICATE",
}

interface TestPopupState {
    action: TestPopupActions;
    id: string;
    name: string;
    schoolClassId?: string;
}

export const useTestOperations = (schoolClassId?: string) => {
    const t = useT();
    const history = useHistory();
    const { setCreatingForSchoolClassId } = useGeneralContext();

    const t_sharedWithClass = t("Shared with class", { _tags: "ShareFlow, ShareToast" });
    const t_linkCopied = t("Link copied – You can paste it with Ctrl+V/Cmd+V.", {
        _tags: "ShareFlow, ShareToast",
    });
    const t_visibilityUpdated = t("Visibility updated", { _tags: "ClassActivity, EditActivityVisibilityModal" });
    const t_errEditVisibility = t("There was an error while updating the visibility. Please try again.", {
        _tags: "ClassActivity, EditActivityVisibilityModal",
    });
    const {
        loadTestForEdition,
        studentTestContentWrapper,
        duplicateTest,
        deleteTest,
        shareTest,
        updateSharedTestProperties,
        userTests,
        updateTestSchoolClass,
        openWarningModal,
    } = useTestCreationContext();
    const { logEvent } = useLogger();

    const { schoolClasses, createSchoolClass, deleteSchoolClass, hasLoadedSchoolClasses, schoolClassesLoadError } =
        useRTContext();

    const t_defaultClassName = t("My newest class", { _tags: "ShareFlow, useTestOperations" });
    const t_errCreate = t("There was an error while creating the new class. The test was not shared.", {
        _tags: "ShareFlow, useTestOperations",
    });
    const t_errUpdateWithRollback = t(
        "There was an error while assigning the class to the test. The newly created class has been removed and the test was not shared.",
        { _tags: "ShareFlow, useTestOperations" }
    );
    const t_errUpdateNoRollback = t(
        "There was an error while assigning the class to the test. The test was not shared.",
        { _tags: "ShareFlow, useTestOperations" }
    );
    const t_errShareWithRollback = t(
        "There was an error while sharing the test. The newly created class has been removed.",
        { _tags: "ShareFlow, useTestOperations" }
    );
    const t_errShareNoRollback = t("There was an error while sharing the test. Please try again.", {
        _tags: "ShareFlow, useTestOperations",
    });
    const t_errTestNeedsClass = t("The test could not be shared because no private class was selected.", {
        _tags: "ShareFlow, useTestOperations",
    });

    const [testPopup, setTestPopup] = useState<TestPopupState | null>(null);
    const [keepClassOnDuplicate, setKeepClassOnDuplicate] = useState(false);
    const [downloadPopupTestDetails, setDownloadPopupTestDetails] = useState<TeacherTestDetails | null>(null);

    const [sharePopup, setSharePopup] = useState<SharePopupState>(INITIAL_SHARE_POPUP_STATE);
    const [shareLinkPopupTestId, setShareLinkPopupTestId] = useState<string | null>(null);
    // Two close calls in the same tick both see the stale open state — the ref dedupes the
    // abandon event where the state guard alone cannot.
    const hasLoggedShareAbandonRef = useRef(false);

    // Commit-guard Sentry dedupe: one event per share-popup session, reset with the popup state.
    const hasReportedShareGuardTripRef = useRef(false);

    const [editTestDetails, setEditTestDetails] = useState<EditTestDetailsState>(INITIAL_EDIT_TEST_DETAILS_STATE);
    const [editTestDetailsInProgress, setEditTestDetailsInProgress] = useState(false);
    const [editActivityVisibilityPopup, setEditActivityVisibilityPopup] = useState<{ testId: string } | null>(null);
    const shareToastState = useToast();
    const [shareToastMessage, setShareToastMessage] = useState("");
    const [shareToastDuration, setShareToastDuration] = useState(SHARE_TOAST_DURATION_CLASS_MS);
    const [shareToastVariant, setShareToastVariant] = useState(ToastVariants.SUCCESS);

    const triggerShareToast = (message: string, duration: number, variant = ToastVariants.SUCCESS) => {
        setShareToastMessage(message);
        setShareToastDuration(duration);
        setShareToastVariant(variant);
        shareToastState.triggerToast();
    };

    const {
        shareInProgress,
        setShareInProgress,
        inviteModal,
        setInviteModal,
        handleCloseInviteModal,
        handleInviteNavigate,
        handleSelectShareClass,
        handleCloseNewClassForm,
        handleCollapseNewClassForm,
        handleNewClassNameChange,
        handleToggleNewClassConsent,
        handleSelectShareTiming,
        handleShareVisibilityDateChange,
    } = useShareFlowState(setSharePopup);

    const shareDetailHandlers = useMemo(() => _makeDetailHandlers(setSharePopup), []);
    const editDetailHandlers = useMemo(() => _makeDetailHandlers(setEditTestDetails), []);

    const handleOpenTestPreview = async (testId: string) => {
        await loadTestForEdition(testId, true);

        logEvent("Opened preview", {
            testId,
            origin: schoolClassId ? "class details" : "overview",
        });

        history.push(`/create/preview?mode=${PreviewView.MOBILE}`, {
            urlBeforePreview: getCreationReturnPath(schoolClassId ?? ""),
        });
    };

    const handleOpenPdfConfiguration = async (testId: string) => {
        await loadTestForEdition(testId, true);

        logEvent("Opened configuration", {
            testId,
            origin: schoolClassId ? "class details" : "overview",
        });

        history.push(`/create/preview?mode=${PreviewView.PDF}`, {
            urlBeforePreview: getCreationReturnPath(schoolClassId ?? ""),
        });
    };

    const handleEditTest = async (testId: string, schoolClassId?: string) => {
        await loadTestForEdition(testId);

        if (schoolClassId) {
            setCreatingForSchoolClassId(schoolClassId);
        }

        logEvent("Vokabeltest v2 Click Edit", {
            testId,
        });

        history.push("/create/summary");
    };

    const isTestShared = (testId: string | undefined): boolean => {
        if (!testId) return false;
        return studentTestContentWrapper.hasOwnProperty(testId);
    };

    const handleOpenDuplicateTestPopup = (
        testId: string,
        testName: string,
        schoolClassId?: string,
        keepClassByDefault = false
    ) => {
        if (!testId) return;
        setKeepClassOnDuplicate(keepClassByDefault);
        setTestPopup({ action: TestPopupActions.DUPLICATE, id: testId, name: testName, schoolClassId });
    };

    const toggleKeepClassOnDuplicate = () => setKeepClassOnDuplicate((prev) => !prev);

    const handleConfirmDuplicateTest = async (newName: string) => {
        if (testPopup?.action !== TestPopupActions.DUPLICATE) return;
        const schoolClassId = keepClassOnDuplicate ? testPopup.schoolClassId : undefined;
        await duplicateTest(testPopup.id, newName, schoolClassId);
        setTestPopup(null);
    };

    const handleOpenDeleteTestPopup = (testId: string, testName: string) => {
        if (!testId) return;
        setTestPopup({ action: TestPopupActions.DELETE, id: testId, name: testName });
    };

    const handleConfirmDeleteTest = async () => {
        if (testPopup?.action !== TestPopupActions.DELETE) return;
        await deleteTest(testPopup.id);
        setTestPopup(null);
    };

    const handleShareTest = (testId: string | undefined, preAssignedSchoolClassId?: string) => {
        if (!testId) return;
        logEvent("started share flow", {
            activity_type: ActivityTypeProps.TEST,
            test_id: testId,
        });
        hasLoggedShareAbandonRef.current = false;
        const test = userTests.find((t) => t.id === testId);
        setSharePopup((prev) => ({
            ...prev,
            testId,
            testName: test?.name ?? "",
            selectedSchoolClassId: preAssignedSchoolClassId ?? test?.groupId ?? null,
        }));
    };

    const _resetSharePopupState = () => {
        hasReportedShareGuardTripRef.current = false;
        setSharePopup(INITIAL_SHARE_POPUP_STATE);
    };

    // Public close for the popup's onClose prop (X / overlay / Abbrechen). Internal completion paths
    // call _resetSharePopupState directly because they run while shareInProgress is still true.
    const handleCloseSharePopup = () => {
        if (shareInProgress) return;
        // Guard against a close on an already-closed popup reporting a phantom abandon.
        if (sharePopup.testId && !hasLoggedShareAbandonRef.current) {
            hasLoggedShareAbandonRef.current = true;
            logEvent("abandoned share flow", {
                activity_type: ActivityTypeProps.TEST,
                last_step: SHARE_STEP_PROPS[sharePopup.step],
                has_class_selected: hasEffectiveClass(sharePopup.selectedSchoolClassId, sharePopup.newClassForm),
            });
        }
        _resetSharePopupState();
    };

    const _advanceStep = (prev: SharePopupState, direction: 1 | -1): SharePopupState => {
        // Forward Next from an open form commits the draft, which supersedes any prior selectedSchoolClassId.
        const committingNewClass = prev.step === ShareSteps.CLASS && direction === 1 && !!prev.newClassForm?.isOpen;
        // The skip decision reads the committed form: a class created on this very Next is an effective
        // class, so the teacher still gets the timing step instead of silently sharing right away.
        const stateForSkip =
            committingNewClass && prev.newClassForm
                ? { ...prev, newClassForm: collapseClassForm(prev.newClassForm, true) }
                : prev;

        let index = _getAdjacentStepIndex(prev.step, direction);
        let skippedTiming = false;
        while (index !== null && SHARE_STEP_ORDER[index] === ShareSteps.TIMING && _shouldSkipTimingStep(stateForSkip)) {
            skippedTiming = true;
            index = _getAdjacentStepIndex(SHARE_STEP_ORDER[index], direction);
        }
        if (index === null) return prev;
        const leftClass = prev.step === ShareSteps.CLASS && SHARE_STEP_ORDER[index] !== ShareSteps.CLASS;
        const nextNewClassForm =
            leftClass && prev.newClassForm
                ? collapseClassForm(prev.newClassForm, committingNewClass)
                : prev.newClassForm;
        return {
            ...prev,
            step: SHARE_STEP_ORDER[index],
            // Skipping timing means the teacher never saw the choice in this flow, so a "Later" picked
            // before switching to practice + no class must not silently schedule the share.
            timing: skippedTiming ? ShareTimings.NOW : prev.timing,
            visibilityDate: skippedTiming ? null : prev.visibilityDate,
            newClassForm: nextNewClassForm,
            selectedSchoolClassId: committingNewClass ? null : prev.selectedSchoolClassId,
        };
    };

    const handleShareNextStep = () => {
        setSharePopup((prev) => _advanceStep(prev, 1));
    };

    const handleSharePrevStep = () => {
        setSharePopup((prev) => _advanceStep(prev, -1));
    };

    const isFinalShareAction = sharePopup.step === ShareSteps.DETAILS;

    const handleSharePrimaryAction = () => {
        if (isFinalShareAction) {
            void handleConfirmShare();
        } else {
            handleShareNextStep();
        }
    };

    const handleSelectShareMode = (shareMode: ShareModes) => {
        setSharePopup((prev) => {
            const nextMode = prev.selectedMode === shareMode ? null : shareMode;
            // Switching to a different mode invalidates the draft; toggling-off (nextMode null) preserves it.
            const switchedToDifferentMode =
                nextMode !== null && prev.selectedMode !== null && nextMode !== prev.selectedMode;
            // The class selection deliberately survives a mode switch: the mode step disables TEST whenever the
            // selection can't take one, so a contradictory pair is unreachable and the class step keeps showing
            // the teacher's pick — only which classes are selectable changes.
            return {
                ...prev,
                selectedMode: nextMode,
                newClassForm: switchedToDifferentMode ? null : prev.newClassForm,
            };
        });
    };

    const handleOpenNewClassForm = () => {
        // Only the first open is an intent to create — re-opening a collapsed draft is navigation.
        if (!sharePopup.newClassForm) {
            logEvent("started class creation", { source: "share_flow_test" });
        }
        setSharePopup((prev) => {
            if (prev.newClassForm) {
                return { ...prev, newClassForm: { ...prev.newClassForm, isOpen: true } };
            }
            const existingNames = schoolClasses.map((c) => c.name);
            const defaultName = getUniqueDefaultClassName(t_defaultClassName, existingNames);
            return {
                ...prev,
                newClassForm: {
                    name: defaultName,
                    hasConsent: false,
                    isOpen: true,
                    isCommitted: false,
                    committedName: null,
                },
            };
        });
    };

    const handleToggleShareInfo = () => {
        setSharePopup((prev) => ({ ...prev, infoExpanded: !prev.infoExpanded }));
    };

    const handleCopyTestLink = (testId: string | undefined) => {
        if (!testId) return;
        setShareLinkPopupTestId(testId);
    };

    const handleCloseShareLinkPopup = () => {
        setShareLinkPopupTestId(null);
    };

    const handleTestLinkCopied = (testId: string | null) => {
        if (!testId) return;
        logEvent("VTG - Assignment Link Copied", {
            testId,
        });
    };

    const handleShareButtonClick = (testId: string | undefined) => {
        if (!testId) return;

        if (isTestShared(testId)) {
            handleCopyTestLink(testId);
        } else {
            handleShareTest(testId);
        }
    };

    const handleConfirmShare = async () => {
        if (!sharePopup.testId || !sharePopup.selectedMode) return;
        const testId = sharePopup.testId;
        const selectedMode = sharePopup.selectedMode;
        // Commit-boundary guard: the UI keeps this state unreachable — if a TEST share without a
        // resolvable private class fires anyway (stale token, class list still loading or failed to
        // load), block it before any backend mutation instead of regressing silently.
        if (
            requiresPrivateClass(ClassActivityCategory.TEST, selectedMode) &&
            !hasEffectivePrivateClass(sharePopup.selectedSchoolClassId, sharePopup.newClassForm, schoolClasses)
        ) {
            // The toast re-fires on every click, but repeated clicks on the same broken state must not
            // burst the alert channel — Sentry gets one event per popup session.
            if (!hasReportedShareGuardTripRef.current) {
                hasReportedShareGuardTripRef.current = true;
                Sentry.captureException(new Error("Share blocked: TEST mode without a private class"), {
                    extra: {
                        testId,
                        selectedSchoolClassId: sharePopup.selectedSchoolClassId,
                        hasCommittedNewClass: !!sharePopup.newClassForm?.isCommitted,
                        knownSchoolClassCount: schoolClasses.length,
                    },
                });
            }
            triggerShareToast(t_errTestNeedsClass, SHARE_TOAST_DURATION_ERROR_MS, ToastVariants.ERROR);
            return;
        }
        const test = userTests.find((t) => t.id === testId);
        const previousSchoolClassId = test?.groupId ?? null;

        const submitNow = moment();
        const plannedDate: Moment =
            sharePopup.timing === ShareTimings.LATER && sharePopup.visibilityDate
                ? sharePopup.visibilityDate
                : submitNow;
        const visibilityDate = toVisibilityDateMs(plannedDate, submitNow);
        // A date clamped back to submitNow shares right away, so it must not be logged as scheduled.
        const isScheduled = visibilityDate !== null && visibilityDate > submitNow.valueOf();

        setShareInProgress(true);

        const result = await executeShareWithClassRollback({
            activityId: testId,
            selectedSchoolClassId: sharePopup.selectedSchoolClassId,
            previousSchoolClassId,
            newClassForm: sharePopup.newClassForm,
            createSchoolClass,
            deleteSchoolClass,
            updateActivitySchoolClass: updateTestSchoolClass,
            shareActivity: async (effectiveSchoolClassId) => {
                // Deadline and duration only apply in TEST mode — practice skips step 3 and hides step-4 add-buttons.
                const isTestMode = selectedMode === ShareModes.TEST;
                const serialized = _serializeDetails(sharePopup);
                const sharingData: StudentTestSharingData = {
                    visibilityDate,
                    dueDate: isTestMode ? serialized.dueDate : 0,
                    timeLimit: isTestMode ? serialized.timeLimit : "",
                    teacherComment: serialized.teacherComment,
                };
                await shareTest({
                    testId,
                    testInfo: sharingData,
                    shareMode: selectedMode,
                    effectiveSchoolClassId,
                    options: { silentOnError: true },
                });
            },
            getErrorMessage: ({ stage, rolledBack }) => {
                switch (stage) {
                    case ShareWithClassRollbackStages.CREATE:
                        return t_errCreate;
                    case ShareWithClassRollbackStages.UPDATE:
                        return rolledBack ? t_errUpdateWithRollback : t_errUpdateNoRollback;
                    case ShareWithClassRollbackStages.SHARE:
                        return rolledBack ? t_errShareWithRollback : t_errShareNoRollback;
                }
            },
        });

        try {
            if (!result.ok) {
                openWarningModal(result.errorMessage);
                _resetSharePopupState();
                return;
            }

            const { effectiveSchoolClassId, createdSchoolClassId } = result;

            // Deferred from createSchoolClass (suppressCreatedEvent): a rolled-back class must not
            // be counted, so the event fires only once the whole share has succeeded. The render-
            // scoped `schoolClasses` is still the pre-create list here.
            if (createdSchoolClassId) {
                logEvent("Created Class", {
                    groupId: createdSchoolClassId,
                    class_id: createdSchoolClassId,
                    class_visibility: toClassVisibilityProp(SchoolClassVisibilities.PRIVATE),
                    ...toIsFirstClassProps(hasLoadedSchoolClasses && !schoolClassesLoadError, schoolClasses.length),
                });
            }

            // Delivery of an activity to a class is captured here via hasClass; no separate
            // "assigned" event — it would duplicate this exact moment.
            logEvent("VTG - Online Task Shared", {
                // testId is grandfathered — kept so the existing series stays continuous. test_id is
                // the canonical name `started share flow` uses, so the share funnel can be
                // constrained to a single test across its steps.
                testId,
                test_id: testId,
                shareMode: selectedMode,
                isScheduled,
                hasDeadline: sharePopup.deadline.show,
                hasDuration: sharePopup.duration.show,
                hasClass: effectiveSchoolClassId !== null,
                // Which class received the share. Omitted on a class-less share rather than sent
                // empty, so the property never carries a value that isn't a real class.
                ...(effectiveSchoolClassId ? { class_id: effectiveSchoolClassId } : {}),
            });

            const invite = await resolveEmptyClassInviteModal({
                effectiveSchoolClassId,
                // Only set when this share created the class; otherwise the name comes from schoolClasses.
                createdClassName: createdSchoolClassId ? sharePopup.newClassForm?.name.trim() : undefined,
                schoolClasses,
            });

            if (invite) {
                setInviteModal(invite);
            } else if (effectiveSchoolClassId !== null) {
                triggerShareToast(t_sharedWithClass, SHARE_TOAST_DURATION_CLASS_MS);
            } else {
                navigator.clipboard.writeText(getStudentTestUrl(testId)).catch(() => {});
                triggerShareToast(t_linkCopied, SHARE_TOAST_DURATION_LINK_COPIED_MS);
            }
            _resetSharePopupState();
        } finally {
            setShareInProgress(false);
        }
    };

    const handleNavigateToReview = (testId: string | undefined) => {
        if (!testId) return;
        history.push(`/review/${testId}`);
    };

    const handleOpenEditTestDetails = (testId: string | undefined) => {
        if (!testId) return;
        const sharedTest = studentTestContentWrapper[testId];
        const parsedTime = _parseTimeLimit(sharedTest?.timeLimit);
        setEditTestDetails({
            testId,
            // Legacy shares resolve to TEST, so their deadline/duration stay editable.
            shareMode: sharedTest ? resolveShareModeWithLegacy(sharedTest) : ShareModes.TEST,
            // Trimmed so legacy whitespace-only comments pre-fill the textarea as empty.
            message: sharedTest?.teacherComment?.trim() ?? "",
            deadline: {
                show: !!sharedTest?.dueDate,
                value: sharedTest?.dueDate ? moment(sharedTest.dueDate) : null,
            },
            duration: {
                show: !!parsedTime,
                hours: parsedTime?.hours ?? INITIAL_TIME_VALUE,
                minutes: parsedTime?.minutes ?? INITIAL_TIME_VALUE,
            },
        });
    };

    const handleCloseEditTestDetails = () => {
        if (editTestDetailsInProgress) return;
        setEditTestDetails(INITIAL_EDIT_TEST_DETAILS_STATE);
    };

    const handleSaveEditTestDetails = async () => {
        if (!editTestDetails.testId) return;

        const sharedTest = studentTestContentWrapper[editTestDetails.testId];
        if (!sharedTest) return;

        setEditTestDetailsInProgress(true);
        try {
            // Pass visibilityDate through untouched — omitting it lets updateSharedTestProperties write
            // null and silently flip the test's visibility to HIDDEN.
            const didUpdate = await updateSharedTestProperties(editTestDetails.testId, {
                ..._serializeDetails(editTestDetails),
                visibilityDate: sharedTest.visibilityDate,
            });
            if (didUpdate) setEditTestDetails(INITIAL_EDIT_TEST_DETAILS_STATE);
        } finally {
            setEditTestDetailsInProgress(false);
        }
    };

    const handleOpenEditActivityVisibility = (testId: string) => {
        setEditActivityVisibilityPopup({ testId });
    };

    const handleCloseEditActivityVisibility = () => {
        setEditActivityVisibilityPopup(null);
    };

    const handleSaveTestVisibility = async (params: { visibilityDate: number | null }) => {
        if (!editActivityVisibilityPopup) return;
        const testId = editActivityVisibilityPopup.testId;
        const sharedTest = studentTestContentWrapper[testId];
        // Close the modal first so any error-path warning surfaces alone instead of stacking on top.
        handleCloseEditActivityVisibility();
        // Without the existing record we'd PUT zero/empty for dueDate/timeLimit/comment and clobber BE state.
        if (!sharedTest) {
            openWarningModal(t_errEditVisibility);
            return;
        }
        const success = await updateSharedTestProperties(testId, {
            visibilityDate: params.visibilityDate,
            // `|| 0` not `?? 0`: a legacy record read back from the BE can still carry "".
            dueDate: sharedTest.dueDate || 0,
            timeLimit: sharedTest.timeLimit ?? "",
            teacherComment: sharedTest.teacherComment ?? "",
        });
        if (!success) return;
        triggerShareToast(t_visibilityUpdated, SHARE_TOAST_DURATION_CLASS_MS);
    };

    return {
        userTests,

        // Test Management
        handleOpenTestPreview,
        handleOpenPdfConfiguration,
        handleEditTest,
        isTestShared,
        handleOpenDuplicateTestPopup,
        handleConfirmDuplicateTest,
        keepClassOnDuplicate,
        toggleKeepClassOnDuplicate,
        handleOpenDeleteTestPopup,
        handleConfirmDeleteTest,
        testPopup,
        setTestPopup,
        downloadPopupTestDetails,
        setDownloadPopupTestDetails,

        // Test Sharing
        handleShareTest,
        handleCloseSharePopup,
        handleConfirmShare,
        handleCopyTestLink,
        handleCloseShareLinkPopup,
        handleTestLinkCopied,
        handleShareButtonClick,
        handleNavigateToReview,
        sharePopup,
        shareLinkPopupTestId,
        shareInProgress,
        shareToast: {
            isOpen: shareToastState.isOpen,
            onOpenChange: shareToastState.onOpenChange,
            message: shareToastMessage,
            duration: shareToastDuration,
            variant: shareToastVariant,
        },

        handleSharePrimaryAction,
        isFinalShareAction,
        handleSharePrevStep,
        handleSelectShareMode,
        handleSelectShareClass,
        handleOpenNewClassForm,
        handleCloseNewClassForm,
        handleCollapseNewClassForm,
        handleNewClassNameChange,
        handleToggleNewClassConsent,
        handleToggleShareInfo,
        handleSelectShareTiming,
        handleShareVisibilityDateChange,
        shareDetailHandlers,

        inviteModal,
        handleCloseInviteModal,
        handleInviteNavigate,

        // Test Details Editing
        handleOpenEditTestDetails,
        handleCloseEditTestDetails,
        handleSaveEditTestDetails,
        editDetailHandlers,
        editTestDetails,
        editTestDetailsInProgress,

        editActivityVisibilityPopup,
        handleOpenEditActivityVisibility,
        handleCloseEditActivityVisibility,
        handleSaveTestVisibility,
    };
};
